import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { errorMessage } from '../../lib/toast';
import { getVendor, getHistory, listOf } from './vendorsApi';
import useVendorAccess from './useVendorAccess';
import VendorActions, { UploadDocumentButton, acknowledgeVendor, vendorPermissions } from './VendorActions';
import { VENDOR_ERRORS, atLeast } from './vendorLabels';
import { SpecialityChips, Skeleton, FullError } from './VendorBits';
import { VendorAttention, VendorCompliance, buildSignals } from './VendorStatus';
import { DocumentsSection, FactsSection } from './VendorDetailPanels';
import VendorActivity from './VendorActivity';
import './Vendors.css';

// Nexus proposals for this vendor. Shared by the attention block (pending
// changes) and the activity rail, so it loads once.
function useHistory(vendorId, reloadToken) {
  const [state, setState] = useState({ status: 'loading', items: [] });
  useEffect(() => {
    let live = true;
    setState((s) => ({ status: 'loading', items: s.items }));
    getHistory(vendorId)
      .then((d) => { if (live) setState({ status: 'ready', items: listOf(d, 'proposals') }); })
      .catch(() => { if (live) setState({ status: 'error', items: [] }); });
    return () => { live = false; };
  }, [vendorId, reloadToken]);
  return state;
}

/**
 * /vendors/:id. Leads with whether the vendor is in order (what needs doing, then
 * its compliance standing), then the reference facts beside a single activity
 * timeline. Comments, documents and history are live reads with their own errors.
 */
export default function VendorDetailPage() {
  const { id } = useParams();
  const vendorId = Number(id);
  const [state, setState] = useState({ status: 'loading', data: null, message: '' });
  const [panel, setPanel] = useState(null); // 'edit' | 'archive' | 'review'
  const [docsReload, setDocsReload] = useState(0);
  const [historyReload, setHistoryReload] = useState(0);
  const { me } = useVendorAccess();
  const history = useHistory(vendorId, historyReload);
  const reqId = useRef(0);

  const load = useCallback(() => {
    const n = ++reqId.current;
    setState((s) => ({ ...s, status: 'loading' }));
    getVendor(vendorId)
      .then((data) => { if (n === reqId.current) setState({ status: 'ready', data, message: '' }); })
      .catch((err) => {
        if (n !== reqId.current) return;
        const message = err?.response?.status === 404
          ? VENDOR_ERRORS.not_found
          : "Couldn't load vendors. Try again in a minute.";
        setState({ status: 'error', data: null, message: errorMessage(err, message, VENDOR_ERRORS) });
      });
  }, [vendorId]);

  useEffect(() => { load(); }, [load]);

  const back = <Link className="vd-back" to="/vendors">&larr; All vendors</Link>;

  if (state.status === 'loading' && !state.data) {
    return <div className="main-content-inner vd-page">{back}<Skeleton label="Loading vendor" /></div>;
  }
  if (state.status === 'error') {
    return (
      <div className="main-content-inner vd-page">
        {back}
        <FullError message={state.message} onRetry={state.message === VENDOR_ERRORS.not_found ? null : load} />
      </div>
    );
  }

  const {
    vendor, level, review_status: reviewStatus, effective_next_review: effectiveNextReview, iso_fields_known: isoKnown,
  } = state.data;
  const perms = vendorPermissions(vendor, level);
  const signals = buildSignals({
    vendor, reviewStatus, effectiveNextReview, history, perms,
  });
  // A review or a decision changes both the vendor row and its history.
  const refreshAll = () => { load(); setHistoryReload((n) => n + 1); };
  const onSignal = (action) => {
    if (action === 'acknowledge') acknowledgeVendor(vendor.id, load);
    else setPanel(action);
  };

  return (
    <div className="main-content-inner vd-page vd-detail">
      {back}
      <header className="vd-detail-head">
        <div className="vd-detail-title">
          <h1 dir="auto">{vendor.name}</h1>
          <div className="vd-detail-meta">
            <SpecialityChips items={vendor.specialities} emptyLabel="No speciality yet" />
            {vendor.active === false && <span className="vd-status vd-status--muted">Archived</span>}
          </div>
        </div>
        <VendorActions
          vendor={vendor}
          level={level}
          me={me}
          isoFieldsKnown={isoKnown}
          panel={panel}
          onPanel={setPanel}
          onReviewed={refreshAll}
          onProposed={() => setHistoryReload((n) => n + 1)}
        />
      </header>

      <VendorAttention signals={signals} onAction={onSignal} />
      <VendorCompliance vendor={vendor} isoFieldsKnown={isoKnown} reviewStatus={reviewStatus} effectiveNextReview={effectiveNextReview} />

      <div className="vd-detail-grid">
        <div className="vd-detail-main">
          <DocumentsSection
            vendorId={vendorId}
            reloadToken={docsReload}
            action={perms.canPropose ? <UploadDocumentButton vendorId={vendorId} onUploaded={() => setDocsReload((n) => n + 1)} /> : null}
          />
          <FactsSection vendor={vendor} />
        </div>
        <aside className="vd-detail-rail">
          <VendorActivity vendorId={vendorId} canComment={atLeast(level, 'comment')} history={history} />
        </aside>
      </div>
    </div>
  );
}
